import { isEducationEnvironmentDisabled } from '../../utilities/manualInputUtilities';
import { NPSESchoolType } from '../NonPublicStudentFormViewModel';
import { VALIDATION_MESSAGE } from '../ValidationMessage';
import { createDateSchema } from './CustomValidation/DateExtendedSchema';
import yup from './CustomValidation/yup-extended';
import { DateSchemaType, ExtendedDateSchemaType } from './ValidationTypes';

export const NonPublicStudentEntryValidationSchema = yup.object().shape(
  {
    studentIdentifier: yup
      .string()
      .required(VALIDATION_MESSAGE.REQUIRED)
      .mustBeNumber()
      .min(9, VALIDATION_MESSAGE.EXACT_NUMBER_LENGTH(9))
      .max(9, VALIDATION_MESSAGE.EXACT_NUMBER_LENGTH(9)),
    lastOrSurname: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
    firstName: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
    birthDate: (createDateSchema() as ExtendedDateSchemaType)
      .max('2999-12-31', VALIDATION_MESSAGE.MUST_BE_VALID_DATE)
      .required(VALIDATION_MESSAGE.REQUIRED),
    personIdentifier: yup.string(),
    personIdentifierSsn: yup.string().isSSN(),
    schoolType: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
    schoolTypeInitial: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
    sex: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
    hispanicOrLatinoEthnicity: yup
      .string()
      .required(VALIDATION_MESSAGE.REQUIRED),
    race: yup.string().when('hispanicOrLatinoEthnicity', {
      is: 'No',
      then: (schema) => schema.required(VALIDATION_MESSAGE.REQUIRED),
      otherwise: (schema) => schema.notRequired(),
    }),
    specialEducation: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
    englishLearnerStatus: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
    enrollmentEntryDate: (createDateSchema() as ExtendedDateSchemaType)
      .max('2999-12-31', VALIDATION_MESSAGE.MUST_BE_VALID_DATE)
      .required(VALIDATION_MESSAGE.REQUIRED),
    entryType: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
    enrollmentStatus: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
    gradeLevel: yup.string().required(VALIDATION_MESSAGE.REQUIRED),

    // Used for UI purposes:
    hasMatch: yup.boolean().required(VALIDATION_MESSAGE.REQUIRED).isTrue(),
    ageEvaluationYear: yup.string(),

    singleParentOrSinglePregnantWomanStatus: yup.string().when('schoolType', {
      is: NPSESchoolType.CTE,
      then: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
      otherwise: yup.string().notRequired(),
    }),
    cteaeDisplacedHomemakerIndicator: yup.string().when('schoolType', {
      is: NPSESchoolType.CTE,
      then: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
      otherwise: yup.string().notRequired(),
    }),

    eligibilityDeterminationDate: (createDateSchema() as ExtendedDateSchemaType)
      .max('2999-12-31', VALIDATION_MESSAGE.MUST_BE_VALID_DATE)
      .when('schoolType', {
        is: NPSESchoolType.SPED,
        then: (schema: DateSchemaType) =>
          (schema as ExtendedDateSchemaType).required(
            VALIDATION_MESSAGE.REQUIRED
          ),
        otherwise: (schema: DateSchemaType) =>
          (schema as ExtendedDateSchemaType).notRequired(),
      }),
    iepStartDate: (createDateSchema() as ExtendedDateSchemaType)
      .max('2999-12-31', VALIDATION_MESSAGE.MUST_BE_VALID_DATE)
      .when('schoolType', {
        is: NPSESchoolType.SPED,
        then: (schema: DateSchemaType) =>
          (schema as ExtendedDateSchemaType).required(
            VALIDATION_MESSAGE.REQUIRED
          ),
        otherwise: (schema: DateSchemaType) =>
          (schema as ExtendedDateSchemaType).notRequired(),
      }),
    projectedHSDiplomaType: yup.string().when(['schoolType', 'gradeLevel'], {
      is: (schoolType: string, gradeLevel: string) =>
        schoolType === NPSESchoolType.SPED &&
        ['09', '10', '11', '12', 'AE'].includes(gradeLevel),
      then: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
      otherwise: yup.string().notRequired(),
    }),
    programParticipationExitDate: (
      createDateSchema() as ExtendedDateSchemaType
    ).max('2999-12-31', VALIDATION_MESSAGE.MUST_BE_VALID_DATE),
    primaryDisabilityType: yup.string().when('schoolType', {
      is: NPSESchoolType.SPED,
      then: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
      otherwise: yup.string().notRequired(),
    }),
    significantCognitiveDisabilityIndicator: yup.string().when('schoolType', {
      is: NPSESchoolType.SPED,
      then: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
      otherwise: yup.string().notRequired(),
    }),
    ideaEducationalEnvironment: yup
      .string()
      .when(['schoolType', 'birthDate', 'ageEvaluationYear'], {
        is: (
          schoolType: string,
          birthDate: string,
          ageEvaluationYear: string
        ): boolean => {
          return (
            schoolType === NPSESchoolType.SPED &&
            ageEvaluationYear !== undefined &&
            ageEvaluationYear.length > 0 &&
            birthDate !== undefined &&
            birthDate.length > 0 &&
            !isEducationEnvironmentDisabled(ageEvaluationYear, birthDate)
          );
        },
        then: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
        otherwise: yup.string().notRequired(),
      }),
    relatedServices: yup.array().notRequired(),

    outcomeAEntryScore: yup.string(),
    outcomeAEntryScoreDate: (createDateSchema() as ExtendedDateSchemaType)
      .max('2999-12-31', VALIDATION_MESSAGE.MUST_BE_VALID_DATE)
      .when('outcomeAEntryScore', {
        is: (entryScore: string) => entryScore && entryScore.length > 0,
        then: (schema: DateSchemaType) =>
          (schema as ExtendedDateSchemaType).required(
            VALIDATION_MESSAGE.REQUIRED
          ),
        otherwise: (schema: DateSchemaType) =>
          (schema as ExtendedDateSchemaType).notRequired(),
      }),
    outcomeAExitScore: yup.string(),
    outcomeAExitScoreDate: (createDateSchema() as ExtendedDateSchemaType)
      .max('2999-12-31', VALIDATION_MESSAGE.MUST_BE_VALID_DATE)
      .when('outcomeAExitScore', {
        is: (exitScore: string) => exitScore && exitScore.length > 0,
        then: (schema: DateSchemaType) =>
          (schema as ExtendedDateSchemaType).required(
            VALIDATION_MESSAGE.REQUIRED
          ),
        otherwise: (schema: DateSchemaType) =>
          (schema as ExtendedDateSchemaType).notRequired(),
      }),
    outcomeAProgress: yup
      .string()
      .when(['outcomeAEntryScore', 'outcomeAExitScore'], {
        is: (entryScore: string, exitScore: string) =>
          entryScore &&
          entryScore.length > 0 &&
          exitScore &&
          exitScore.length > 0,
        then: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
        otherwise: yup.string().notRequired(),
      }),

    outcomeBEntryScore: yup.string(),
    outcomeBEntryScoreDate: (createDateSchema() as ExtendedDateSchemaType)
      .max('2999-12-31', VALIDATION_MESSAGE.MUST_BE_VALID_DATE)
      .when('outcomeBEntryScore', {
        is: (entryScore: string) => entryScore && entryScore.length > 0,
        then: (schema: DateSchemaType) =>
          (schema as ExtendedDateSchemaType).required(
            VALIDATION_MESSAGE.REQUIRED
          ),
        otherwise: (schema: DateSchemaType) =>
          (schema as ExtendedDateSchemaType).notRequired(),
      }),
    outcomeBExitScore: yup.string(),
    outcomeBExitScoreDate: (createDateSchema() as ExtendedDateSchemaType)
      .max('2999-12-31', VALIDATION_MESSAGE.MUST_BE_VALID_DATE)
      .when('outcomeBExitScore', {
        is: (exitScore: string) => exitScore && exitScore.length > 0,
        then: (schema: DateSchemaType) =>
          (schema as ExtendedDateSchemaType).required(
            VALIDATION_MESSAGE.REQUIRED
          ),
        otherwise: (schema: DateSchemaType) =>
          (schema as ExtendedDateSchemaType).notRequired(),
      }),
    outcomeBProgress: yup
      .string()
      .when(['outcomeBEntryScore', 'outcomeBExitScore'], {
        is: (entryScore: string, exitScore: string) =>
          entryScore &&
          entryScore.length > 0 &&
          exitScore &&
          exitScore.length > 0,
        then: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
        otherwise: yup.string().notRequired(),
      }),

    outcomeCEntryScore: yup.string(),
    outcomeCEntryScoreDate: (createDateSchema() as ExtendedDateSchemaType)
      .max('2999-12-31', VALIDATION_MESSAGE.MUST_BE_VALID_DATE)
      .when('outcomeCEntryScore', {
        is: (entryScore: string) => entryScore && entryScore.length > 0,
        then: (schema: DateSchemaType) =>
          (schema as ExtendedDateSchemaType).required(
            VALIDATION_MESSAGE.REQUIRED
          ),
        otherwise: (schema: DateSchemaType) =>
          (schema as ExtendedDateSchemaType).notRequired(),
      }),
    outcomeCExitScore: yup.string(),
    outcomeCExitScoreDate: (createDateSchema() as ExtendedDateSchemaType)
      .max('2999-12-31', VALIDATION_MESSAGE.MUST_BE_VALID_DATE)
      .when('outcomeCExitScore', {
        is: (exitScore: string) => exitScore && exitScore.length > 0,
        then: (schema: DateSchemaType) =>
          (schema as ExtendedDateSchemaType).required(
            VALIDATION_MESSAGE.REQUIRED
          ),
        otherwise: (schema: DateSchemaType) =>
          (schema as ExtendedDateSchemaType).notRequired(),
      }),
    outcomeCProgress: yup
      .string()
      .when(['outcomeCEntryScore', 'outcomeCExitScore'], {
        is: (entryScore: string, exitScore: string) =>
          entryScore &&
          entryScore.length > 0 &&
          exitScore &&
          exitScore.length > 0,
        then: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
        otherwise: yup.string().notRequired(),
      }),
  },
  [
    ['schoolType', 'relatedServices'],
    ['schoolType', 'ideaEducationalEnvironment'],
    ['gradeLevel', 'relatedServices'],
    ['gradeLevel', 'ideaEducationalEnvironment'],
    ['ageEvaluationYear', 'ideaEducationalEnvironment'],
    ['birthDate', 'ideaEducationalEnvironment'],
    ['outcomeAEntryScore', 'outcomeAEntryScoreDate'],
    ['outcomeAExitScore', 'outcomeAExitScoreDate'],
    ['outcomeAEntryScore', 'outcomeAProgress'],
    ['outcomeAExitScore', 'outcomeAProgress'],
    ['outcomeBEntryScore', 'outcomeBEntryScoreDate'],
    ['outcomeBExitScore', 'outcomeBExitScoreDate'],
    ['outcomeBEntryScore', 'outcomeBProgress'],
    ['outcomeBExitScore', 'outcomeBProgress'],
    ['outcomeCEntryScore', 'outcomeCEntryScoreDate'],
    ['outcomeCExitScore', 'outcomeCExitScoreDate'],
    ['outcomeCEntryScore', 'outcomeCProgress'],
    ['outcomeCExitScore', 'outcomeCProgress'],
  ]
);

/* istanbul ignore next */
export const NPSE_CTEOnlyValidationSchema = yup.object().shape({
  singleParentOrSinglePregnantWomanStatus: yup
    .string()
    .required(VALIDATION_MESSAGE.REQUIRED),
  cteaeDisplacedHomemakerIndicator: yup
    .string()
    .required(VALIDATION_MESSAGE.REQUIRED),
});

/* istanbul ignore next */
export const NPSE_SPEDOnlyValidationSchema = yup.object().shape({
  eligibilityDeterminationDate: (createDateSchema() as ExtendedDateSchemaType)
    .max('2999-12-31', VALIDATION_MESSAGE.MUST_BE_VALID_DATE)
    .required(VALIDATION_MESSAGE.REQUIRED),
  iepStartDate: (createDateSchema() as ExtendedDateSchemaType)
    .max('2999-12-31', VALIDATION_MESSAGE.MUST_BE_VALID_DATE)
    .required(VALIDATION_MESSAGE.REQUIRED),
  primaryDisabilityType: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
  significantCognitiveDisabilityIndicator: yup
    .string()
    .required(VALIDATION_MESSAGE.REQUIRED),
  relatedServices: yup.array().notRequired(),
});
